import { GlobalStore } from '@roc/feature-app-core';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { PayoffService } from '../services/payoffService';
import {
  PaReconciliationData,
  PaReconciliationLoanInfo,
  PaReconciliationRecord,
  PaReconciliationSummaryRow,
  PaReconciliationTotals,
  sumCurrencyFields,
} from '../components/paReconciliation/paReconciliationTypes';

export class PaReconciliationStore {
  private globalStore: GlobalStore;
  payoffService: PayoffService;

  loanInfo: PaReconciliationLoanInfo;
  records: PaReconciliationRecord[];
  selectedType: string | null;

  constructor(
    globalStore: GlobalStore
  ) {
    this.globalStore = globalStore;
    this.payoffService = new PayoffService();
    this.setDefaults();
    makeObservable(this, {
      loanInfo: observable,
      records: observable,
      selectedType: observable,
      initialize: flow,
      getPaReconciliationData: flow,
      reset: action,
      setSelectedType: action,
      clearSelectedType: action,
      markWrittenOff: action,
      markReimbursed: action,
      recordCount: computed,
      totals: computed,
      summaryRows: computed,
      selectedTypeRecords: computed,
    });
  }

  setDefaults() {
    this.loanInfo = null;
    this.records = [];
    this.selectedType = null;
  }

  reset() {
    this.setDefaults();
  }

  setSelectedType(type: string) {
    this.selectedType = type;
  }

  clearSelectedType() {
    this.selectedType = null;
  }

  *initialize(loanId?: number) {
    this.reset();
    yield this.getPaReconciliationData(loanId);
  }

  *getPaReconciliationData(loanId?: number) {
    try {
      const response = yield this.payoffService.getPaReconciliationData(loanId);
      const data: PaReconciliationData = response?.data?.data;
      this.loanInfo = data?.loanInfo ?? null;
      this.records = data?.records ?? [];
    } catch (e) {
      console.log(e);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while getting PA reconciliation data',
      });
    }
  }

  markWrittenOff(rowId: string) {
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'Invoice marked as written off',
    });
  }

  markReimbursed(rowId: string) {
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'Invoice marked as reimbursed',
    });
  }

  get recordCount(): number {
    return this.records.length;
  }

  get totals(): PaReconciliationTotals {
    return sumCurrencyFields(this.records);
  }

  // One row per fee type present in the records, in the order the backend returns them
  get summaryRows(): PaReconciliationSummaryRow[] {
    const types = Array.from(new Set(this.records.map(record => record.type ?? '-')));
    return types.map(type => {
      const rows = this.records.filter(record => (record.type ?? '-') === type);
      return { type, recordCount: rows.length, ...sumCurrencyFields(rows) };
    });
  }

  get selectedTypeRecords(): PaReconciliationRecord[] {
    return this.selectedType ? this.getRecordsForType(this.selectedType) : [];
  }

  getRecordsForType(type: string): PaReconciliationRecord[] {
    return this.records.filter(record => record.type === type);
  }
}

export default PaReconciliationStore;
